import { createContext, useContext, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Button,
  IconButton,
  Link,
  makeStyles,
  Paper,
  Typography,
} from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import LinkIcon from '@material-ui/icons/Link';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { useRouteRef } from '@backstage/core-plugin-api';
import { Provenance, ProductsData, Scope } from '../model/types';
import { safeWebUrl } from '../model/model';
import { productPaths } from '../paths';
import { rootRouteRef } from '../routes';
import {
  eyebrowStyle,
  headingColor,
  headingSize,
  icon,
  linkCardStyle,
  linkColor,
  text,
  tintTileStyle,
} from './styleTokens';

/** Link builders rooted wherever the app mounts the Products page. */
export function useProductPaths() {
  return productPaths(useRouteRef(rootRouteRef)());
}
export const DataContext = createContext<ProductsData | undefined>(undefined);
export const useProducts = () => {
  const value = useContext(DataContext);
  if (!value) throw new Error('Product data is not available');
  return value;
};
export const useStyles = makeStyles(theme => ({
  page: {
    padding: theme.spacing(3),
    maxWidth: 1560,
    gridArea: 'pageContent',
    alignSelf: 'start',
    overflowWrap: 'anywhere',
    minWidth: 0,
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box',
    '& a:focus-visible, & button:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 3,
    },
    '& a, & button': {
      transition:
        'color 120ms ease, background-color 120ms ease, border-color 120ms ease',
    },
    '@media (prefers-reduced-motion: reduce)': {
      '& a, & button': { transition: 'none' },
    },
    [theme.breakpoints.down('sm')]: { padding: theme.spacing(2, 2, 10) },
  },
  heading: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    alignItems: 'flex-start',
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1),
  },
  subtitle: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.75),
    fontSize: text.body,
    lineHeight: 1.6,
    maxWidth: 880,
  },
  // Page heading: the title on its own line, its description below it,
  // smaller and grey.
  pageTitle: {
    fontSize: text.page,
    fontWeight: 600,
    lineHeight: 1.25,
    letterSpacing: '-.4px',
    color: headingColor(theme),
    [theme.breakpoints.down('xs')]: { fontSize: text.section },
  },
  pageSubtitle: {
    marginTop: theme.spacing(0.75),
    maxWidth: 880,
    fontSize: text.emphasis,
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
  },
  // Quiet line under the page subtitle, e.g. a source marker.
  headingMeta: { marginTop: theme.spacing(1) },
  // The same two-tone treatment at section size. Spacing belongs to the container.
  sectionIntro: {
    maxWidth: 880,
    fontSize: headingSize.subsection,
    lineHeight: 1.45,
    letterSpacing: '-.2px',
  },
  sectionIntroLarge: {
    fontSize: headingSize.section,
    lineHeight: 1.36,
    letterSpacing: '-.4px',
  },
  headlineTitle: {
    display: 'inline',
    font: 'inherit',
    fontWeight: 600,
    color: headingColor(theme),
  },
  headlineRest: {
    display: 'inline',
    font: 'inherit',
    fontWeight: 400,
    color: theme.palette.text.secondary,
  },
  portfolioSection: { marginBottom: theme.spacing(4) },
  portfolioHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    paddingBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '& h2': { margin: 0 },
  },
  portfolioList: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))',
    gap: theme.spacing(0.5, 3),
    paddingTop: theme.spacing(1.5),
  },
  portfolioItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    padding: theme.spacing(1, 1.25),
    margin: theme.spacing(0, -1.25),
    borderRadius: theme.shape.borderRadius,
    '&:hover, &:focus-visible': {
      textDecoration: 'none',
      backgroundColor: alpha(theme.palette.primary.main, 0.07),
    },
    '&:hover > :first-child': { textDecoration: 'underline' },
  },
  portfolioName: {
    fontSize: text.lead,
    fontWeight: 600,
    color: linkColor(theme),
  },
  portfolioDescription: {
    fontSize: text.small,
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  eyebrow: { ...eyebrowStyle(theme), marginBottom: theme.spacing(0.75) },
  // Eyebrow used as the label at the top of a side menu.
  navLabel: { padding: theme.spacing(1.5) },
  layout: {
    display: 'grid',
    gridTemplateColumns: '176px minmax(0, 1fr)',
    '& > *': { minWidth: 0 },
    gap: theme.spacing(3),
    [theme.breakpoints.down('md')]: { gridTemplateColumns: '1fr' },
  },
  nav: {
    alignSelf: 'start',
    padding: theme.spacing(1),
    borderRight: `1px solid ${theme.palette.divider}`,
    '& a': { borderRadius: theme.shape.borderRadius, marginBottom: 3 },
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 4,
    },
  },
  navItem: {
    fontSize: text.small,
    '&.Mui-selected': {
      backgroundColor: alpha(theme.palette.primary.main, 0.11),
      color: headingColor(theme),
    },
  },
  // Passed to ListItemText as classes.primary.
  navItemText: { fontSize: text.small },
  // Side menu inside a product tab (Technical, Industry): no box, a teal bar
  // marks the current view.
  tabPanel: { marginTop: theme.spacing(3) },
  sideNav: {
    padding: 0,
    border: 0,
    background: 'transparent',
    boxShadow: 'none',
    [theme.breakpoints.down('md')]: { maxWidth: 340, width: '100%' },
  },
  sideNavItem: {
    borderLeft: '3px solid transparent',
    '&[aria-current="page"], &[aria-current="page"]:hover': {
      borderLeftColor: theme.palette.primary.main,
      backgroundColor: alpha(theme.palette.primary.main, 0.09),
    },
  },
  tabHeading: {
    marginTop: 0,
    marginBottom: theme.spacing(2.5),
    alignItems: 'baseline',
    gap: theme.spacing(1.5),
  },
  toolbar: {
    display: 'flex',
    gap: theme.spacing(1.5),
    alignItems: 'center',
    flexWrap: 'wrap',
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2.5),
    borderRadius: theme.shape.borderRadius * 2,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: 'none',
  },
  search: { flex: '1 1 260px' },
  filter: { width: 210, [theme.breakpoints.down('xs')]: { width: '100%' } },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 260px), 1fr))',
    gap: theme.spacing(2),
  },
  // Directory product card. Behaviour comes from linkCard; this is layout only.
  card: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 234,
    '& $eyebrow': { marginBottom: 0 },
  },
  cardBody: {
    padding: theme.spacing(2.5, 2.5, 1.5),
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  cardTitle: { fontWeight: 600, fontSize: text.title, lineHeight: 1.4 },
  cardDescription: {
    fontSize: text.small,
    color: theme.palette.text.secondary,
    lineHeight: 1.6,
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  cardFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    alignItems: 'center',
    padding: theme.spacing(1, 2.5, 2.5),
  },
  // Clickable cards (products, offerings). Mark the arrow with data-arrow and,
  // where one link should cover the card, mark it with data-stretched.
  linkCard: linkCardStyle(theme),
  // Title colours come from linkCardStyle's data-stretched rule.
  linkTitle: { letterSpacing: '-0.2px' },
  linkCta: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: text.small,
    fontWeight: 600,
    color: linkColor(theme),
  },
  // Initials tile that anchors a card when no icon is recorded.
  initialsTile: {
    display: 'grid',
    placeItems: 'center',
    width: 36,
    height: 36,
    marginBottom: theme.spacing(1.5),
    ...tintTileStyle(theme),
    fontSize: text.small,
    fontWeight: 700,
    letterSpacing: '.5px',
  },
  countPill: {
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: 999,
    fontSize: text.meta,
    lineHeight: '20px',
    // Its own weight, so a bold surrounding row does not make it bold.
    fontWeight: 400,
    color: theme.palette.text.secondary,
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    whiteSpace: 'nowrap',
    // A pill that is also a link reads as one.
    '&[href]': { fontWeight: 600, color: linkColor(theme) },
    '&[href]:hover': {
      textDecoration: 'none',
      backgroundColor: alpha(theme.palette.primary.main, 0.16),
    },
  },
  productWebsite: {
    marginTop: 'auto',
    paddingTop: theme.spacing(1),
    '& a': { color: theme.palette.text.secondary },
    '& a:hover': { color: linkColor(theme) },
  },
  muted: {
    color: theme.palette.text.secondary,
    fontSize: text.meta,
    lineHeight: 1.5,
  },
  smallLink: {
    fontSize: text.meta,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    overflowWrap: 'anywhere',
    '& svg': { fontSize: icon.inline },
  },
  sectionHead: {
    flexWrap: 'wrap',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    margin: theme.spacing(2.5, 0, 1.5),
  },
  // Headings inside panels and lists (h3/h4); section headings use SectionIntro.
  sectionTitle: { fontSize: text.emphasis, fontWeight: 600, lineHeight: 1.5 },
  table: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: 'none',
    overflowX: 'auto',
    '& table': { minWidth: 640 },
    '& th': {
      fontWeight: 600,
      fontSize: text.meta,
      textTransform: 'none',
      letterSpacing: 0,
      color: theme.palette.text.secondary,
      background: theme.palette.background.default,
    },
    '& td': {
      fontSize: text.body,
      lineHeight: 1.5,
      padding: theme.spacing(1.5),
      verticalAlign: 'top',
    },
    '& th:first-child, & td:first-child': { paddingLeft: theme.spacing(2) },
  },
  cellSecondary: {
    color: theme.palette.text.secondary,
    fontSize: text.meta,
    marginTop: 4,
    lineHeight: 1.4,
  },
  scope: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  empty: {
    padding: theme.spacing(3),
    textAlign: 'left',
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: 'none',
    borderRadius: theme.shape.borderRadius * 2,
  },
  quiet: {
    marginTop: theme.spacing(2.5),
    paddingTop: theme.spacing(1.5),
    borderTop: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
    fontSize: text.meta,
  },
  productTabs: {
    minHeight: 44,
    borderBottom: `1px solid ${theme.palette.divider}`,
    '& [role="tab"]': {
      color: theme.palette.text.secondary,
      opacity: 1,
      '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.04),
        color: theme.palette.text.primary,
      },
      '&:focus-visible': { outlineOffset: -3 },
      minWidth: 0,
      minHeight: 44,
      padding: '12px 20px',
      fontSize: text.body,
      textTransform: 'none',
      [theme.breakpoints.down('xs')]: {
        padding: '10px 12px',
        fontSize: text.small,
      },
    },
    '& [aria-selected="true"]': {
      fontWeight: 600,
      color: linkColor(theme),
      background: 'transparent',
    },
  },
  offeringGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
    gap: theme.spacing(2),
  },
  // Layout only; border, radius and shadow come from linkCard.
  offeringCard: {
    display: 'flex',
    flexDirection: 'column',
    padding: 24,
    [theme.breakpoints.down('xs')]: { padding: 20 },
    minWidth: 0,
  },
  offeringTitle: {
    fontSize: text.title,
    fontWeight: 600,
    lineHeight: '26px',
    marginBottom: theme.spacing(1),
  },
  offeringDescription: {
    fontSize: text.body,
    lineHeight: '23px',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  offeringActions: {
    marginTop: 'auto',
    paddingTop: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
  },
  reviewCard: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2.5),
    [theme.breakpoints.down('xs')]: { padding: 20 },
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: 'none',
    '& > h4': { fontSize: text.emphasis, lineHeight: 1.5 },
    '& > fieldset': { margin: '16px 0 12px' },
    '& legend': { fontSize: text.small },
    '& [role="radiogroup"]': { gap: '0 12px' },
  },
  reviewEvidence: {
    marginTop: 16,
    padding: '12px 16px',
    background: alpha(theme.palette.primary.main, 0.04),
    borderRadius: theme.shape.borderRadius,
    '& > div': { margin: '8px 0 0', gap: 16 },
    '& a': { fontSize: text.meta },
  },
  reviewActions: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 16,
    marginTop: 20,
    paddingTop: 16,
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  reviewNotice: {
    fontSize: text.small,
    lineHeight: '21px',
    color: theme.palette.text.secondary,
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    padding: '8px 16px',
    marginBottom: 24,
  },
  reviewTools: {
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: 16,
    '& > :first-child': { flex: '1 1 220px' },
  },
  reviewSaved: {
    color: theme.palette.text.secondary,
    fontSize: text.meta,
    lineHeight: '20px',
    marginTop: 12,
  },
  panel: {
    padding: theme.spacing(3),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: 'none',
  },
}));
export function useQuery() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const update = (
    values: Record<string, string | string[] | null>,
    replace = true,
  ) => {
    const next = new URLSearchParams(location.search);
    Object.entries(values).forEach(([key, value]) => {
      next.delete(key);
      if (Array.isArray(value)) value.forEach(v => next.append(key, v));
      else if (value) next.set(key, value);
    });
    navigate(
      { pathname: location.pathname, search: next.toString() },
      { replace },
    );
  };
  return { params, update, search: location.search };
}
export function useScope(): Scope {
  const { params } = useQuery();
  return {
    software: params.getAll('software'),
    offerings: params.getAll('offering'),
    search: params.get('q') || '',
  };
}
export function CopyLink() {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  return (
    <IconButton
      aria-label={copied ? 'Link copied' : 'Copy page link'}
      title={copied ? 'Copied' : 'Copy page link'}
      onClick={copy}
    >
      <LinkIcon fontSize="small" />
    </IconButton>
  );
}
export function ExternalLink({
  href,
  children,
}: {
  href?: string;
  children: React.ReactNode;
}) {
  const classes = useStyles();
  const url = safeWebUrl(href);
  return url ? (
    <Link
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={classes.smallLink}
    >
      {children}
      <OpenInNewIcon />
    </Link>
  ) : (
    <span>{children}</span>
  );
}
export function Empty({
  title,
  children,
}: {
  title: string;
  children?: React.ReactNode;
}) {
  const classes = useStyles();
  return (
    <Paper className={classes.empty}>
      <Typography variant="h6" component="h3">
        {title}
      </Typography>
      {children && (
        <Typography className={classes.subtitle}>{children}</Typography>
      )}
    </Paper>
  );
}
export function SnapshotNote() {
  const { snapshot } = useProducts();
  const classes = useStyles();
  const paths = useProductPaths();
  return (
    <div className={classes.quiet}>
      Product registry as of {snapshot.registry.generatedAt}
      {snapshot.branches
        ? ` · repository branches checked ${snapshot.branches.capturedAt}`
        : ''}
      .{' '}
      <Link
        component={RouterLink}
        to={paths.directory(new URLSearchParams({ view: 'attention' }))}
      >
        See records needing review
      </Link>
    </div>
  );
}
/** Up to two initials from a name, for cards without a recorded icon. */
export const initials = (name: string) => {
  const words = name
    .normalize('NFC')
    .replace(/\(.*?\)/g, '')
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean)
    .slice(0, 2);
  const letters =
    words.length === 1 ? words[0].slice(0, 2) : words.map(w => w[0]).join('');
  // Fall back to the name's first letter, e.g. a name that is only "(Legacy)".
  return (letters || name.match(/[\p{L}\p{N}]/u)?.[0] || '').toUpperCase();
};
const asSentence = (title: string) =>
  /[.!?]$/.test(title) ? title : `${title}.`;
export function PageHeading({
  title,
  subtitle,
  eyebrow,
  action,
  meta,
}: {
  title: string;
  subtitle?: string;
  eyebrow?: string;
  action?: React.ReactNode;
  meta?: React.ReactNode;
}) {
  const classes = useStyles();
  return (
    <div className={classes.heading}>
      <div>
        {eyebrow && (
          <Typography className={classes.eyebrow}>{eyebrow}</Typography>
        )}
        <Typography component="h1" className={classes.pageTitle}>
          {title}
        </Typography>
        {subtitle && (
          <Typography className={classes.pageSubtitle}>{subtitle}</Typography>
        )}
        {meta && <div className={classes.headingMeta}>{meta}</div>}
      </div>
      {action}
    </div>
  );
}
/** Section heading with its description continuing inline in grey. */
export function SectionIntro({
  title,
  children,
  component = 'h2',
  id,
  size = 'subsection',
}: {
  title: string;
  children?: React.ReactNode;
  component?: 'h2' | 'h3';
  id?: string;
  /** `section` for page-level sections (home); `subsection` within a tab. */
  size?: 'section' | 'subsection';
}) {
  const classes = useStyles();
  return (
    <div
      className={`${classes.sectionIntro} ${
        size === 'section' ? classes.sectionIntroLarge : ''
      }`}
    >
      <Typography
        id={id}
        component={component}
        className={classes.headlineTitle}
      >
        {asSentence(title)}
      </Typography>
      {children && (
        <Typography component="p" className={classes.headlineRest}>
          {' '}
          {children}
        </Typography>
      )}
    </div>
  );
}
export function ProductLink({
  id,
  children,
  tab = 'overview',
  stretched = false,
}: {
  id: string;
  children: React.ReactNode;
  tab?: string;
  /** Cover the whole surrounding linkCard with this link. */
  stretched?: boolean;
}) {
  const paths = useProductPaths();
  return (
    <Link
      component={RouterLink}
      to={paths.product(id, tab)}
      {...(stretched ? { 'data-stretched': true } : {})}
    >
      {children}
    </Link>
  );
}
const markerLabel = (layers: Set<string>) => {
  if (layers.size > 1) return 'From catalog + curated';
  return layers.has('catalog') ? 'From catalog' : 'Curated';
};
/**
 * Says which layer supplied a group of values: the catalog, the curated
 * registry file, or both. Hover shows the sources. Renders nothing when no
 * value in the group was recorded.
 */
export function SourceMarker({
  provenance,
  variant = 'pill',
  suffix,
}: {
  provenance: (Provenance | undefined)[];
  /** `text` for quiet secondary lines such as table cells. */
  variant?: 'pill' | 'text';
  /** Appended to the text variant, e.g. a catalog-link caveat. */
  suffix?: string;
}) {
  const classes = useStyles();
  const recorded = provenance.filter((p): p is Provenance => !!p?.layer);
  if (!recorded.length) return null;
  const label = markerLabel(new Set(recorded.map(p => p.layer!)));
  const title = [...new Set(recorded.map(p => p.source))].join(' · ');
  return variant === 'text' ? (
    <div className={classes.cellSecondary} title={title}>
      {suffix ? `${label} · ${suffix}` : label}
    </div>
  ) : (
    <span className={classes.countPill} title={title}>
      {label}
    </span>
  );
}
/** A count shown as a soft pill; pass `to` to make it a link. */
export function CountPill({
  children,
  to,
}: {
  children: React.ReactNode;
  to?: string;
}) {
  const classes = useStyles();
  return to ? (
    <Link component={RouterLink} to={to} className={classes.countPill}>
      {children}
    </Link>
  ) : (
    <span className={classes.countPill}>{children}</span>
  );
}
export function BackToProducts() {
  const paths = useProductPaths();
  return (
    <Button size="small" component={RouterLink} to={paths.directory()}>
      ← All products
    </Button>
  );
}
